import { lazy, Suspense, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { closeSearch, getSearchOpenState, isTypingTarget, openSearch, registerSearchHost, useSearchOpenState } from '../../lib/searchOpen'

// The universal search panel is its own chunk: nothing of it loads until the
// first open.
const SearchPanel = lazy(() => import('./SearchPanel'))

/**
 * Mounted once by HeaderV2, OUTSIDE HeaderShell (which must stay free of
 * router and browser dependencies for the prerender). Owns the keyboard
 * shortcuts and portals the panel to <body> while open.
 *
 * - Cmd/Ctrl-K toggles the search from anywhere, inputs included.
 * - `/` opens it only when focus is not in an input, textarea, select or
 *   contenteditable (the Judge composer and Deck Chat are textareas), and no
 *   modal dialog is open.
 * - Any navigation closes it (Back included).
 * - openCardSheet (src/lib/searchOpen.ts) opens it on one card's sheet.
 */
export default function SearchHost() {
  const { open, mode, card, cardSeq } = useSearchOpenState()
  const location = useLocation()

  // Lets openCardSheet's callers know a panel will actually show.
  useEffect(() => registerSearchHost(), [])

  useEffect(() => {
    closeSearch()
  }, [location.key])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      const key = e.key.toLowerCase()
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && key === 'k') {
        e.preventDefault()
        if (getSearchOpenState().open) closeSearch()
        else openSearch()
        return
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (getSearchOpenState().open) return
        if (isTypingTarget(document.activeElement)) return
        if (document.querySelector('[aria-modal="true"]')) return
        e.preventDefault()
        openSearch()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  if (!open || typeof document === 'undefined') return null
  return createPortal(
    <Suspense fallback={null}>
      <SearchPanel initialMode={mode} initialCard={card} cardSeq={cardSeq} onClose={closeSearch} />
    </Suspense>,
    document.body,
  )
}
